import React, { useState } from 'react';
import { Icon } from 'react-icons-kit';
import { clock } from 'react-icons-kit/feather/clock';
import { phone } from 'react-icons-kit/feather/phone';
import { mail } from 'react-icons-kit/feather/mail';
import { target } from 'react-icons-kit/feather/target';
import logo from '../assets/images/trustymoWhite.png';
import ResidenceSlider from './ResidenceSlider';
import SavingsResultCard from './SavingsResultCard';
import { calculateSavings } from './calculateSavings';

const DEMO_URL = 'https://trustymo.com/contactez-nous';

function SavingsSimulatorPage() {
  const [residenceCount, setResidenceCount] = useState(25);
  const savings = calculateSavings(residenceCount);

  const travelSubtext =
    savings.workWeeks > 0
      ? `Soit plus de ${savings.workWeeks} semaine${savings.workWeeks > 1 ? 's' : ''} de travail récupérées pour vos équipes.`
      : 'Des déplacements optimisés pour vos équipes.';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-primary py-4 px-4">
        <div className="max-w-5xl mx-auto">
          <a href="https://trustymo.com">
            <img src={logo} alt="logo Trustymo" className="h-10 object-cover" />
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        <section className="text-center mb-12">
          <span className="inline-block px-4 py-1 rounded-full bg-secondary/20 text-secondary text-xs font-semibold tracking-wider mb-6">
            SIMULATEUR D&apos;ÉCONOMIES
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Découvrez le temps et les ressources que vous pouvez{' '}
            <span className="text-primary">économiser</span>
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Estimez les gains que Trustymo peut apporter à vos équipes en simplifiant la
            communication au sein de vos résidences.
          </p>
        </section>

        <section className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <div className="flex flex-col md:flex-row md:gap-8">
            <div className="flex-1 md:pr-8 md:border-r md:border-gray-100">
              <p className="text-gray-800 font-medium mb-2">
                Nombre de résidences que vous gérez
              </p>
              <p className="mb-6">
                <span className="text-4xl font-bold text-primary">{residenceCount}</span>{' '}
                <span className="text-gray-800 font-medium">résidences</span>
              </p>
              <ResidenceSlider value={residenceCount} onChange={setResidenceCount} />
            </div>

            <div className="flex-1 mt-8 md:mt-0">
              <div className="bg-secondary/10 rounded-xl p-6 h-full">
                <p className="font-semibold text-gray-800 mb-2">
                  Comment avons-nous calculé ces économies ?
                </p>
                <p className="text-sm text-gray-600">
                  Nos estimations sont basées sur des moyennes observées chez nos clients :
                  réduction des déplacements, des appels répétitifs et du temps consacré à traiter les emails. 
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Avec Trustymo, vous pouvez économiser{' '}
            <span className="text-primary underline decoration-primary decoration-2 underline-offset-4">
              chaque année
            </span>{' '}
            :
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <SavingsResultCard
              icon={clock}
              value={savings.formattedTravelHours}
              label="heures de déplacements économisées"
              subtext={travelSubtext}
            />
            <SavingsResultCard
              icon={phone}
              value={savings.formattedCalls}
              label="appels répétitifs économisés"
              subtext="Moins d'interruptions, plus de temps pour les missions à valeur ajoutée."
            />
            <SavingsResultCard
              icon={mail}
              value={savings.formattedEmails}
              label="emails en moins à traiter"
              subtext="Une boîte mail allégée et une communication centralisée et efficace."
            />
          </div>
        </section>

        <section className="bg-secondary/10 rounded-2xl p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <Icon icon={target} size={28} className="text-secondary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Plus de temps, plus de satisfaction
                </h3>
                <p className="text-gray-600 text-sm">
                  En centralisant l'information et en fluidifiant les échanges, vous améliorez la qualité de service tout en renforçant la satisfaction de vos résidents.
                </p>
              </div>
            </div>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-secondary transition duration-300 whitespace-nowrap"
            >
              Demander une démo →
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SavingsSimulatorPage;
